/**
 * INKO — Fantasy Academy mini-hra "Uhodni hráče podle indícií".
 * Pure frontend MVP: 10 kol, 30 s na hráče, indície se odhalují po 5 s.
 * Body: 100 → 80 → 60 → 40 → 20 podle počtu odhalených indícií při
 * správné odpovědi. Špatná odpověď / skip / timeout = 0 bodů.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, ArrowClockwise, Play, SkipForward, Prohibit,
    Trophy, Check, X, Lightbulb, Clock, Star, Flag, Gauge, Eye, Globe,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import playersData from "../data/inko-players.json";
import managersData from "../data/inko-managers.json";
import painData from "../data/inko-pain.json";
import {
    normalize, isCorrect, isCorrectFuzzy, buildOptions,
    extractKeywords, autocompleteSearch, pacingConfig,
    microClueFor, MICRO_CLUE_LABELS, shuffleMicroClues, getPornoClues,
    surnameHintFor, SURNAME_LEAK_FIELDS,
    isManager, computeCorrectScore, maybeInjectManager, pickRoundEntities,
    isPain,
    computeRoundBonuses, computeEndGameBonuses, computeTitles, advanceChainState,
    paidSurnameHint, paidSurnameHintCost, PAID_HINT_MIN_CLUE,
    INKO_TIERS, getTier, getTierById,
    getCrossFantasyProfile, getCrossFantasyLore,
    SCORE_TABLE, READING_MODES, READING_MODE_LABELS,
} from "../lib/inko";
import { ComboFilter } from "../components/inko/ComboFilter";
import { InkoLandingTop3 } from "../components/inko/InkoLandingTop3";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Config -------------------------------------------------------------

const ROUNDS_PER_GAME = 10;
const FEEDBACK_MS = 5000;
const LS_READING_MODE = "inko.reading_mode";
const LS_DISPLAY_NAME = "inko.display_name";
const LS_CLIENT_ID = "inko.client_id";
// Daily-10 once-per-day lock. Map { "YYYY-MM-DD": { score, correct_count, date } }
const LS_DAILY10_PLAYED = "inko.daily10.played";

const getDaily10PlayedMap = () => {
    try {
        return JSON.parse(localStorage.getItem(LS_DAILY10_PLAYED) || "{}");
    } catch {
        return {};
    }
};

const markDaily10Played = (date, payload) => {
    if (!date) return;
    const map = getDaily10PlayedMap();
    // Keep only today + last 3 days to avoid unbounded growth.
    const entries = Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 3);
    const trimmed = Object.fromEntries(entries);
    trimmed[date] = { ...payload, date };
    try {
        localStorage.setItem(LS_DAILY10_PLAYED, JSON.stringify(trimmed));
    } catch {
        // ignore quota errors
    }
};

const ensureClientId = () => {
    let id = localStorage.getItem(LS_CLIENT_ID);
    if (!id) {
        id = (crypto?.randomUUID?.() || (Date.now() + "-" + Math.random().toString(36).slice(2))).replace(/-/g, "").slice(0, 32);
        localStorage.setItem(LS_CLIENT_ID, id);
    }
    return id;
};

// ---- Utils --------------------------------------------------------------

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const pickRoundPlayers = (pool) => {
    // Stratified sampling: 4 current + 6 history za hru → cca 40 % současnosti.
    const current = pool.filter((p) => p.era === "current");
    const history = pool.filter((p) => p.era === "history");
    const needCurrent = 4;
    const needHistory = ROUNDS_PER_GAME - needCurrent;

    const pickedCurrent = shuffle(current).slice(0, Math.min(needCurrent, current.length));
    const pickedHistory = shuffle(history).slice(0, Math.min(needHistory, history.length));

    let picked = [...pickedCurrent, ...pickedHistory];
    if (picked.length < ROUNDS_PER_GAME) {
        const rest = shuffle(pool.filter((p) => !picked.includes(p)));
        picked = [...picked, ...rest.slice(0, ROUNDS_PER_GAME - picked.length)];
    }
    return shuffle(picked);
};

// ---- Main ---------------------------------------------------------------

export default function Inko({ daily10Date: propDaily10Date } = {}) {
    // Support ?date=YYYY-MM-DD in URL (for shareable /inko/daily-10 links).
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const daily10Date = propDaily10Date || params.get("date") || null;
    const isDaily10 = Boolean(propDaily10Date) || (typeof window !== "undefined" && window.location.pathname.startsWith("/inko/daily-10"));

    const [phase, setPhase] = useState("start"); // start | playing | result
    const [mode, setMode] = useState("easy");    // easy | hard
    // Daily-10 has a fixed profile: PORNO + MEDIUM for a fair global leaderboard.
    // Classic (free-play) keeps the user's last chosen reading mode.
    const [readingMode, setReadingMode] = useState(
        isDaily10 ? "porno" : (localStorage.getItem(LS_READING_MODE) || "classic")
    );
    const [roundPlayers, setRoundPlayers] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [results, setResults] = useState([]);
    // Streak chain state — Phase 2/3 graduated INKO Vision / Early Chain bonuses.
    const [chainState, setChainState] = useState({ earlyChain: 0, firstClueChain: 0 });
    const [daily10Pool, setDaily10Pool] = useState(null);  // fetched player[] from backend
    const [daily10Date_, setDaily10Date_] = useState(daily10Date);  // resolved date (backend picks today if null)
    const [daily10Loading, setDaily10Loading] = useState(isDaily10);

    // Fetch daily-10 pool if in daily-10 mode.
    useEffect(() => {
        if (!isDaily10) return;
        setDaily10Loading(true);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/inko/daily10/today${daily10Date ? `?date=${daily10Date}` : ""}`;
        fetch(url)
            .then((r) => r.json())
            .then((data) => {
                setDaily10Date_(data.date);
                const idToPlayer = Object.fromEntries(playersData.players.map((p) => [p.id, p]));
                const pool = (data.player_ids || []).map((pid) => idToPlayer[pid]).filter(Boolean);
                setDaily10Pool(pool);
            })
            .catch(() => setDaily10Pool([]))
            .finally(() => setDaily10Loading(false));
    }, [isDaily10, daily10Date]);

    const startGame = (payload) => {
        // Accept either the legacy `"medium"` mode-string (Daily-10 lock) or a
        // full `{mode, reading_mode}` tier object (Classic tier picker).
        const selectedMode = typeof payload === "object" ? payload.mode : payload;
        const selectedReading = typeof payload === "object" ? payload.reading_mode : null;
        setMode(selectedMode);
        if (selectedReading) {
            setReadingMode(selectedReading);
            localStorage.setItem(LS_READING_MODE, selectedReading);
        }
        let pool;
        if (isDaily10 && daily10Pool && daily10Pool.length >= 10) {
            // Fixed pool — no shuffle so all players playing on the same date
            // face rounds in the same order. Backend already applied its own mix.
            pool = daily10Pool.slice(0, 10);
        } else {
            // Weighted sample: 2% mgr, 5% pain, 3% cross-fantasy, rest classic.
            pool = pickRoundEntities(
                playersData.players,
                managersData.managers || [],
                painData.entries || [],
                { seedKey: isDaily10 && daily10Date_ ? `daily10:${daily10Date_}` : null }
            );
        }
        // Legacy manager injection kept for Daily-10 backend pools that
        // haven't been reprocessed with pickRoundEntities yet — no-op when
        // the pool already contains a manager.
        if (isDaily10 && daily10Pool && daily10Pool.length >= 10) {
            pool = maybeInjectManager(pool, managersData.managers || [], {
                spawnProbability: managersData?.policy?.spawn_probability ?? 0.02,
                seedKey: daily10Date_ ? `daily10:${daily10Date_}` : null,
            });
        }
        // Mark daily-10 as consumed the moment the game starts, so refresh
        // mid-game doesn't grant a second attempt. Score is finalized when
        // the round ends.
        if (isDaily10 && daily10Date_) {
            markDaily10Played(daily10Date_, { score: 0, correct_count: 0 });
        }
        setRoundPlayers(pool);
        setCurrentIdx(0);
        setResults([]);
        setChainState({ earlyChain: 0, firstClueChain: 0 });
        setPhase("playing");
    };

    const finishRound = (result) => {
        const next = [...results, result];
        setResults(next);
        setChainState((prev) => advanceChainState(prev, result));
        if (currentIdx + 1 >= roundPlayers.length) {
            if (isDaily10 && daily10Date_) {
                const baseTotal = next.reduce((s, r) => s + r.score, 0);
                const endBonuses = computeEndGameBonuses(next);
                const endBonusPoints = endBonuses.reduce((s, b) => s + b.points, 0);
                const total = baseTotal + endBonusPoints;
                const correct = next.filter((r) => r.correct).length;
                markDaily10Played(daily10Date_, { score: total, correct_count: correct });
            }
            setPhase("result");
        } else {
            setCurrentIdx(currentIdx + 1);
        }
    };

    const giveUp = () => {
        if (isDaily10 && daily10Date_) {
            const total = results.reduce((s, r) => s + r.score, 0);
            const correct = results.filter((r) => r.correct).length;
            markDaily10Played(daily10Date_, { score: total, correct_count: correct });
        }
        setPhase("result");
    };
    const restart = () => setPhase("start");

    const totalScore = results.reduce((sum, r) => sum + r.score, 0);

    return (
        <AppShell>
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
                <div className="mb-6 flex items-center justify-between">
                    <Link
                        to="/inko"
                        data-testid="inko-back-home"
                        className="font-mono text-[11px] uppercase tracking-widest text-white/50 hover:text-fa-cream flex items-center gap-2"
                    >
                        <ArrowLeft size={12} weight="bold" /> INKO Start
                    </Link>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40" data-testid="inko-mode-badge">
                        {isDaily10
                            ? `Fantasy Academy · INKO Daily 10 · ${daily10Date_ || "…"}`
                            : "Fantasy Academy · INKO"}
                    </div>
                </div>

                {isDaily10 && daily10Loading && (
                    <div
                        className="mb-6 border border-white/10 bg-black/40 p-4 font-mono text-[11px] uppercase tracking-widest text-white/50"
                        data-testid="inko-daily10-loading"
                    >
                        Načítám dnešní pool 10 hráčů…
                    </div>
                )}

                {phase === "start" && (
                    <StartScreen
                        onStart={startGame}
                        readingMode={readingMode}
                        setReadingMode={(m) => {
                            setReadingMode(m);
                            localStorage.setItem(LS_READING_MODE, m);
                        }}
                        isDaily10={isDaily10}
                        daily10Date={daily10Date_}
                        daily10Loading={daily10Loading}
                        daily10AlreadyPlayed={
                            isDaily10 && daily10Date_
                                ? (getDaily10PlayedMap()[daily10Date_] || null)
                                : null
                        }
                    />
                )}

                {phase === "playing" && roundPlayers[currentIdx] && (
                    <Round
                        key={roundPlayers[currentIdx].id + currentIdx + readingMode}
                        player={roundPlayers[currentIdx]}
                        allPlayers={playersData.players}
                        mode={mode}
                        readingMode={readingMode}
                        roundNum={currentIdx + 1}
                        totalRounds={roundPlayers.length}
                        totalScore={totalScore}
                        chainState={chainState}
                        onFinish={finishRound}
                        onGiveUp={giveUp}
                    />
                )}

                {phase === "result" && (
                    <ResultScreen
                        results={results}
                        totalScore={totalScore}
                        mode={mode}
                        readingMode={readingMode}
                        onRestart={restart}
                        isDaily10={isDaily10}
                        daily10Date={daily10Date_}
                    />
                )}
            </div>
        </AppShell>
    );
}

// ---- Start screen -------------------------------------------------------

function StartScreen({ onStart, readingMode, setReadingMode, isDaily10 = false, daily10Date = null, daily10Loading = false, daily10AlreadyPlayed = null }) {
    const pacing = pacingConfig(readingMode);
    const modeDisabled = (isDaily10 && daily10Loading) || Boolean(daily10AlreadyPlayed);
    const [landingTier, setLandingTier] = useState(() => {
        // Default landing preview to Padawan (Medium+Middle) — most popular combo.
        return getTierById(localStorage.getItem("inko.landing_tier") || "padawan") || INKO_TIERS[1];
    });
    const updateLandingTier = (t) => {
        setLandingTier(t);
        try { localStorage.setItem("inko.landing_tier", t.id); } catch {
            // ignore quota
        }
    };
    return (
        <div className="border border-white/10 bg-black/40 p-4 sm:p-8" data-testid="inko-start-screen">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint mb-2">
                    {isDaily10 ? "Denní 10 · sdílená výzva" : "Mini-hra"}
                </div>
                <h1 className="font-mono text-4xl sm:text-5xl uppercase tracking-tight text-fa-cream">
                    {isDaily10 ? "INKO DAILY 10" : "INKO"}
                </h1>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/50">
                    {isDaily10
                        ? `Všichni dnes hrají stejných 10 hráčů · ${daily10Date || "…"}`
                        : "Uhodni Premier League hráče podle indícií"}
                </div>
            </div>

            {!isDaily10 && (
                <Link
                    to="/inko/daily-10"
                    data-testid="inko-goto-daily10"
                    className="mb-4 border border-fa-yellow/50 bg-fa-yellow/10 hover:bg-fa-yellow hover:text-black transition-colors p-4 flex items-center justify-between group"
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow group-hover:text-black">
                            Sdílená výzva
                        </div>
                        <div className="mt-1 font-mono text-lg uppercase text-fa-cream group-hover:text-black">
                            Denní 10 · stejný pool pro všechny
                        </div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-fa-yellow group-hover:text-black">
                        Hrát <Play size={12} weight="fill" />
                    </div>
                </Link>
            )}

            {!isDaily10 && (
                <Link
                    to="/inko/daily"
                    data-testid="inko-goto-daily"
                    className="mb-6 border border-white/15 bg-black/30 hover:bg-black/50 hover:border-fa-cream transition-colors p-4 flex items-center justify-between group"
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 group-hover:text-fa-cream">
                            Denní výzva
                        </div>
                        <div className="mt-1 font-mono text-lg uppercase text-fa-cream">
                            1 hráč dnes · globální žebříček
                        </div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/50 group-hover:text-fa-cream">
                        Hrát <Play size={12} weight="fill" />
                    </div>
                </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <RuleCard icon={Clock} title={`${pacing.round_duration_s} s na hráče`} desc={`Nová indície každých ${pacing.clue_interval_s} s`} />
                <RuleCard icon={Lightbulb} title="6 indícií" desc="Od FPL faktu po přezdívku" />
                <RuleCard icon={Star} title="100 → 10 bodů" desc="Podle rychlosti odhalení" />
            </div>

            {isDaily10 && (
                <ReadingModePicker
                    readingMode={readingMode}
                    setReadingMode={setReadingMode}
                    locked={isDaily10}
                />
            )}

            <InkoLandingTop3 tier={landingTier} onChangeTier={updateLandingTier} />

            {isDaily10 ? (
                <div className="grid grid-cols-1 gap-3">
                    {daily10AlreadyPlayed && (
                        <div
                            data-testid="inko-daily10-already-played"
                            className="border border-fa-yellow/40 bg-fa-yellow/5 p-4 flex items-center justify-between gap-4"
                        >
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow mb-1">
                                    Dnešní pokus vyčerpán
                                </div>
                                <div className="font-mono text-sm text-fa-cream">
                                    Skóre: <span className="text-fa-mint">{daily10AlreadyPlayed.score}</span>
                                    {" · "}
                                    Trefy: <span className="text-fa-mint">{daily10AlreadyPlayed.correct_count}/10</span>
                                </div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Další pokus zítra · nový pool o půlnoci CET
                                </div>
                            </div>
                            <Link
                                to="/inko"
                                data-testid="inko-daily10-back-classic"
                                className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60 hover:border-fa-cream hover:text-fa-cream transition-colors flex items-center gap-2 shrink-0"
                            >
                                Volná hra <Play size={12} weight="fill" />
                            </Link>
                        </div>
                    )}
                    <button
                        type="button"
                        disabled={modeDisabled}
                        onClick={() => onStart("medium")}
                        data-testid="inko-daily10-start-medium-btn"
                        className="border border-fa-cyan/60 bg-fa-cyan/10 hover:bg-fa-cyan hover:text-black transition-colors p-6 text-left group disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-fa-cyan/10 disabled:hover:text-inherit"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-cyan group-hover:text-black">
                                Denní 10 · fixní profil
                            </span>
                            <Play size={16} weight="fill" className="text-fa-cyan group-hover:text-black" />
                        </div>
                        <div className="font-mono text-2xl uppercase tracking-tight text-fa-cream group-hover:text-black">
                            Hrát · Porno · Medium
                        </div>
                        <div className="mt-2 font-mono text-[11px] uppercase tracking-wide text-white/60 group-hover:text-black/70">
                            Stejná obtížnost pro všechny · férový žebříček
                        </div>
                    </button>
                </div>
            ) : (
                <TierPicker onStart={onStart} disabled={modeDisabled} />
            )}

            <div className="mt-6 font-mono text-[10px] uppercase tracking-wide text-white/30">
                Pool: 200 hráčů · 40 % současnost, 60 % legendy · MVP verze
            </div>
        </div>
    );
}

function RuleCard({ icon: Icon, title, desc }) {
    return (
        <div className="border border-white/10 bg-black/60 p-4">
            <Icon size={16} weight="bold" className="text-fa-cream mb-2" />
            <div className="font-mono text-xs uppercase tracking-wide text-fa-cream">
                {title}
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-white/50">
                {desc}
            </div>
        </div>
    );
}

// Colors are hex-encoded per tier so we can drop them into inline styles
// without needing Tailwind's JIT to know every arbitrary utility upfront.
const TIER_STYLES = {
    "fa-mint":   { border: "border-fa-mint",   bg: "bg-fa-mint/10",   accent: "text-fa-mint",   fill: "hover:bg-fa-mint" },
    "fa-cyan":   { border: "border-fa-cyan",   bg: "bg-fa-cyan/10",   accent: "text-fa-cyan",   fill: "hover:bg-fa-cyan" },
    "fa-yellow": { border: "border-fa-yellow", bg: "bg-fa-yellow/10", accent: "text-fa-yellow", fill: "hover:bg-fa-yellow" },
    "orange":    { border: "border-orange-400/60",  bg: "bg-orange-400/10",  accent: "text-orange-300",  fill: "hover:bg-orange-400" },
    "red":       { border: "border-red-400/60",     bg: "bg-red-400/10",     accent: "text-red-300",     fill: "hover:bg-red-400" },
    "fuchsia":   { border: "border-fuchsia-500/60", bg: "bg-fuchsia-500/10", accent: "text-fuchsia-300", fill: "hover:bg-fuchsia-500" },
};

function TierPicker({ onStart, disabled = false }) {
    return (
        <div>
            <div className="mb-3 flex items-center gap-2">
                <Gauge size={12} weight="bold" className="text-fa-cream" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-cream">
                    Vyber úroveň
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {INKO_TIERS.map((t) => {
                    const s = TIER_STYLES[t.color] || TIER_STYLES["fa-cyan"];
                    return (
                        <button
                            key={t.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => onStart({ mode: t.mode, reading_mode: t.reading_mode })}
                            data-testid={`inko-start-tier-${t.id}`}
                            className={`border ${s.border}/60 ${s.bg} ${s.fill} hover:text-black transition-colors p-4 sm:p-5 text-left group disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-inherit`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className={`font-mono text-[9px] uppercase tracking-[0.3em] ${s.accent} group-hover:text-black`}>
                                    Úroveň · #{t.order}
                                </span>
                                <Play size={14} weight="fill" className={`${s.accent} group-hover:text-black`} />
                            </div>
                            <div className="font-mono text-xl sm:text-2xl uppercase tracking-tight text-fa-cream group-hover:text-black">
                                {t.label}
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-wide text-white/60 group-hover:text-black/70">
                                {t.desc}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ReadingModePicker({ readingMode, setReadingMode, locked = false }) {
    if (locked) return null;
    const descriptions = {
        slow: "10 s / indícii · 60 s / hráče · víc času na čtení",
        classic: "5 s / indícii · 30 s / hráče · plné věty",
        keywords: "5 s / indícii · 30 s / hráče · jen holá klíčová slova",
        porno: "5 s / indícii · 30 s / hráče · reálné GW momenty, body, góly",
    };
    return (
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
                <Gauge size={12} weight="bold" className="text-white/40" />
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Rytmus čtení
                </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="inko-reading-mode-picker">
                {READING_MODES.map((m) => {
                    const active = readingMode === m;
                    return (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setReadingMode(m)}
                            data-testid={`inko-reading-mode-${m}`}
                            className={`border p-3 text-left transition-colors ${
                                active
                                    ? "border-fa-cream bg-fa-cream/10 text-fa-cream"
                                    : "border-white/15 text-white/50 hover:border-white/40 hover:text-fa-cream"
                            }`}
                        >
                            <div className="font-mono text-sm uppercase tracking-wide">
                                {READING_MODE_LABELS[m]}
                            </div>
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-white/40">
                                {descriptions[m]}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---- Round --------------------------------------------------------------

function Round({
    player,
    allPlayers,
    mode,
    readingMode,
    roundNum,
    totalRounds,
    totalScore,
    chainState,
    onFinish,
    onGiveUp,
}) {
    const pacing = pacingConfig(readingMode, mode);
    const ROUND_DURATION = pacing.round_duration_s;
    const CLUE_INTERVAL = pacing.clue_interval_s;
    const doKeywords = pacing.keywords;
    const doPorno = pacing.porno;

    const [elapsed, setElapsed] = useState(0);
    const [guess, setGuess] = useState("");
    const [submitted, setSubmitted] = useState(null); // null | { correct, score, guess, reason }
    const [reportedClues, setReportedClues] = useState(new Set());
    // Paid Surname Hint state — once purchased, penalty is subtracted from
    // whatever final score the player earns (correct / wrong / skip / timeout).
    const [hintPurchased, setHintPurchased] = useState(false);
    const hintCost = paidSurnameHintCost(mode);
    const hintValue = useMemo(() => paidSurnameHint(player), [player]);
    const intervalRef = useRef(null);
    const finishTimeoutRef = useRef(null);
    const pendingFinishRef = useRef(null);

    const options = useMemo(
        () => (mode === "hard" ? [] : buildOptions(player, allPlayers, mode, managersData.managers || [])),
        [player, allPlayers, mode]
    );

    const sortedClues = useMemo(
        () => (readingMode === "porno"
            ? getPornoClues(player)
            : [...player.clues].sort((a, b) => a.order - b.order)),
        [player, readingMode]
    );

    // Per-round shuffled micro-clues (shuffled within hard/mid/easy tiers so
    // e.g. CENA isn't always #1). Memoized on player.id so it's stable within
    // a round but changes for each new player.
    const shuffledMicros = useMemo(
        () => shuffleMicroClues(player.micro_clues, player.id),
        [player.id, player.micro_clues]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const cluesRevealed = Math.min(
        Math.floor(elapsed / CLUE_INTERVAL) + 1,
        sortedClues.length
    );
    const currentScore = computeCorrectScore(player, mode, cluesRevealed);
    const timeLeft = Math.max(0, ROUND_DURATION - elapsed);

    // Timer
    useEffect(() => {
        if (submitted) return;
        intervalRef.current = setInterval(() => {
            setElapsed((e) => e + 1);
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, [submitted]);

    // Timeout
    useEffect(() => {
        if (submitted) return;
        if (elapsed >= ROUND_DURATION) {
            handleEnd({ correct: false, score: 0, guess: "", reason: "timeout" });
        }
    }, [elapsed, submitted, ROUND_DURATION]);

    // Cleanup pending finish on unmount
    useEffect(() => {
        return () => {
            clearTimeout(finishTimeoutRef.current);
            clearInterval(intervalRef.current);
        };
    }, []);

    const applyHintPenalty = (baseScore) => (hintPurchased ? baseScore - hintCost : baseScore);

    const handleEnd = ({ correct, score, guess: g, reason }) => {
        if (submitted) return;
        clearInterval(intervalRef.current);
        const bonuses = computeRoundBonuses({ player, correct, cluesRevealed, chainState });
        const bonusPoints = bonuses.reduce((s, b) => s + b.points, 0);
        const finalScore = applyHintPenalty(score + bonusPoints);
        setSubmitted({ correct, score: finalScore, guess: g, reason, bonuses });
        const payload = {
            player,
            guess: g,
            correct,
            score: finalScore,
            cluesRevealed,
            reason,
            hintUsed: hintPurchased,
            bonuses,
        };
        pendingFinishRef.current = payload;
        finishTimeoutRef.current = setTimeout(() => {
            if (pendingFinishRef.current) {
                pendingFinishRef.current = null;
                onFinish(payload);
            }
        }, FEEDBACK_MS);
    };

    const continueNow = () => {
        if (!pendingFinishRef.current) return;
        clearTimeout(finishTimeoutRef.current);
        const payload = pendingFinishRef.current;
        pendingFinishRef.current = null;
        onFinish(payload);
    };

    const handleSubmitGuess = (text) => {
        if (submitted) return;
        const g = (text || "").trim();
        if (!g) return;
        const correct = mode === "hard" ? isCorrectFuzzy(g, player.aliases) : isCorrect(g, player.aliases);
        handleEnd({
            correct,
            score: correct ? currentScore : 0,
            guess: g,
            reason: correct ? "correct" : "wrong",
        });
    };

    const handleSkip = () => handleEnd({ correct: false, score: 0, guess: "", reason: "skip" });

    const handleReportClue = async (order) => {
        if (reportedClues.has(order)) return;
        setReportedClues((prev) => new Set([...prev, order]));
        try {
            let cid = localStorage.getItem("inko.daily.client_id");
            if (!cid) {
                cid = "c-" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
                localStorage.setItem("inko.daily.client_id", cid);
            }
            await axios.post(`${API}/inko/report-clue`, {
                player_id: player.id,
                clue_order: order,
                client_id: cid,
                reason: null,
            });
        } catch {
            // silent
        }
    };

    const progressPct = (elapsed / ROUND_DURATION) * 100;
    const managerMode = isManager(player);
    const painMode = isPain(player);
    // Reveal manager type after clue #1 (per design brief: fair, not hidden)
    const managerRevealed = managerMode && cluesRevealed >= 1;
    // Pain is revealed immediately (brief: karta musí jasně ukazovat typ)
    const painRevealed = painMode;

    return (
        <div className="border border-white/10 bg-black/40" data-testid="inko-round">
            {managerRevealed && (
                <div
                    className="bg-fa-yellow text-black px-4 py-2 flex items-center justify-between gap-3 flex-wrap"
                    data-testid="inko-manager-banner"
                >
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] font-bold">
                        <Star size={12} weight="fill" /> Rare INKO · Type: Manager
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest">
                        Body ×{mode === "hard" ? "2.5" : "2"}
                    </div>
                </div>
            )}
            {painRevealed && (
                <div
                    className="bg-red-500 text-white px-4 py-2 flex items-center justify-between gap-3 flex-wrap"
                    data-testid="inko-pain-banner"
                >
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] font-bold">
                        <Flag size={12} weight="fill" />
                        Death Mode · {player.severity === "legendary" ? "Legendary Pain" : player.severity === "high" ? "High Pain" : "FPL Pain"}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest">
                        Body ×{player.severity === "legendary" ? (mode === "hard" ? "2.5" : "2") : (mode === "hard" ? "1.875" : "1.5")}
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="border-b border-white/10 p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Kolo
                        </div>
                        <div
                            className="font-mono text-2xl text-fa-cream"
                            data-testid="inko-round-num"
                        >
                            {roundNum}
                            <span className="text-white/30 text-lg">/{totalRounds}</span>
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Skóre
                        </div>
                        <div
                            className="font-mono text-2xl text-fa-mint"
                            data-testid="inko-total-score"
                        >
                            {totalScore}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Za trefu
                        </div>
                        <div
                            className="font-mono text-2xl text-fa-yellow"
                            data-testid="inko-current-score"
                        >
                            +{currentScore}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Čas
                        </div>
                        <div
                            className="font-mono text-2xl text-fa-cream tabular-nums"
                            data-testid="inko-timer"
                        >
                            {timeLeft}s
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-white/5">
                <div
                    className="h-full bg-fa-mint transition-all"
                    style={{ width: `${progressPct}%` }}
                />
            </div>

            {/* Clues */}
            <div className="p-3 sm:p-6 space-y-3" data-testid="inko-clues-list">
                {sortedClues.map((clue, i) => {
                    const revealed = i < cluesRevealed;
                    const isReported = reportedClues.has(clue.order);
                    const micro = doKeywords
                        ? (shuffledMicros?.[clue.order - 1] || microClueFor(player, clue.order))
                        : null;
                    const keywords = doKeywords && !micro ? extractKeywords(clue.text) : null;
                    return (
                        <div
                            key={clue.order}
                            data-testid={`inko-clue-${clue.order}`}
                            className={`border p-3 transition-all ${
                                revealed
                                    ? "border-white/20 bg-black/50"
                                    : "border-dashed border-white/5 bg-black/20"
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border shrink-0 ${
                                        revealed
                                            ? "border-fa-mint/40 text-fa-mint"
                                            : "border-white/10 text-white/20"
                                    }`}
                                >
                                    #{clue.order}
                                </div>
                                <div
                                    className={`font-mono text-sm leading-relaxed flex-1 ${
                                        revealed ? "text-fa-cream" : "text-white/10 blur-sm select-none"
                                    }`}
                                >
                                    {revealed ? (
                                        doKeywords ? (
                                            micro ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-fa-yellow/15 border border-fa-yellow/40">
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-fa-yellow/60">
                                                        {MICRO_CLUE_LABELS[micro.field] || micro.field}
                                                    </span>
                                                    <span className="font-mono text-base uppercase tracking-wide text-fa-yellow">
                                                        {SURNAME_LEAK_FIELDS.has(micro.field)
                                                            ? surnameHintFor(player, micro.value)
                                                            : micro.value}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {keywords.map((k, ki) => (
                                                        <span
                                                            key={ki}
                                                            className="inline-block px-2 py-1 bg-fa-yellow/15 border border-fa-yellow/40 text-fa-yellow font-mono text-sm"
                                                        >
                                                            {k}
                                                        </span>
                                                    ))}
                                                </div>
                                            )
                                        ) : (
                                            clue.text
                                        )
                                    ) : (
                                        "•••••••••••••••••••••••••••••"
                                    )}
                                </div>
                                {revealed && (
                                    <button
                                        type="button"
                                        title={isReported ? "Nahlášeno" : "Nahlásit chybu v indícii"}
                                        onClick={() => handleReportClue(clue.order)}
                                        disabled={isReported}
                                        data-testid={`inko-report-clue-${clue.order}`}
                                        className={`shrink-0 p-1 transition-colors ${
                                            isReported
                                                ? "text-fa-yellow/70 cursor-default"
                                                : "text-white/20 hover:text-fa-yellow"
                                        }`}
                                    >
                                        <Flag size={12} weight={isReported ? "fill" : "regular"} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Input area */}
            <div className="border-t border-white/10 p-3 sm:p-6">
                {mode !== "hard" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="inko-options">
                        {options.map((opt) => {
                            const isChosen = submitted && submitted.guess === opt;
                            const isTheAnswer = submitted && isCorrect(opt, player.aliases);
                            let cls = "border-white/15 hover:border-fa-cream hover:bg-fa-cream/5";
                            if (submitted) {
                                if (isTheAnswer) cls = "border-fa-mint bg-fa-mint/20 text-fa-mint";
                                else if (isChosen) cls = "border-red-400 bg-red-400/10 text-red-300";
                                else cls = "border-white/5 text-white/30";
                            }
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    disabled={!!submitted}
                                    onClick={() => handleSubmitGuess(opt)}
                                    data-testid={`inko-option-${normalize(opt)}`}
                                    className={`border p-3 text-left font-mono text-xs sm:text-sm uppercase tracking-wide transition-colors break-words ${cls}`}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <HardInput
                        guess={guess}
                        setGuess={setGuess}
                        onSubmit={handleSubmitGuess}
                        disabled={!!submitted}
                        allPlayers={allPlayers}
                    />
                )}

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    {/* Hint button/chip — full width on mobile so long label doesn't cramp */}
                    <div className="order-2 sm:order-1 w-full sm:w-auto">
                        {hintPurchased ? (
                            <div
                                data-testid="inko-hint-purchased"
                                className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-fa-cream/40 bg-fa-cream/10 text-fa-cream flex flex-wrap items-center gap-2"
                            >
                                <Eye size={12} weight="bold" />
                                Příjmení:
                                <span className="text-fa-yellow" data-testid="inko-hint-value">
                                    {hintValue}
                                </span>
                                <span className="text-white/40">−{hintCost} b</span>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setHintPurchased(true)}
                                disabled={!!submitted || cluesRevealed < PAID_HINT_MIN_CLUE}
                                data-testid="inko-buy-hint-btn"
                                title={cluesRevealed < PAID_HINT_MIN_CLUE
                                    ? `Dostupné až od indície #${PAID_HINT_MIN_CLUE}`
                                    : `Odhalí zamaskované příjmení za −${hintCost} b`}
                                className="w-full sm:w-auto justify-center sm:justify-start font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-fa-cream/30 text-fa-cream/70 hover:border-fa-cream hover:bg-fa-cream/10 hover:text-fa-cream disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                <Eye size={12} weight="bold" />
                                {cluesRevealed < PAID_HINT_MIN_CLUE
                                    ? `Nápověda · od #${PAID_HINT_MIN_CLUE}`
                                    : `Nápověda příjmení (−${hintCost} b)`}
                            </button>
                        )}
                    </div>
                    {/* Skip + give-up — row, split-space on mobile */}
                    <div className="order-1 sm:order-2 flex items-center justify-between sm:justify-end gap-2">
                        <button
                            type="button"
                            onClick={handleSkip}
                            disabled={!!submitted}
                            data-testid="inko-skip-btn"
                            className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60 hover:border-fa-yellow hover:text-fa-yellow disabled:opacity-30 transition-colors flex items-center gap-2"
                        >
                            <SkipForward size={12} weight="bold" /> Skip (0 b)
                        </button>
                        <button
                            type="button"
                            onClick={onGiveUp}
                            disabled={!!submitted}
                            data-testid="inko-giveup-btn"
                            className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60 hover:border-red-400 hover:text-red-300 disabled:opacity-30 transition-colors flex items-center gap-2"
                        >
                            <Prohibit size={12} weight="bold" /> Vzdát se
                        </button>
                    </div>
                </div>
            </div>

            {/* Feedback overlay */}
            {submitted && (
                <div
                    className="border-t border-white/10 p-3 sm:p-6 bg-black/70"
                    data-testid="inko-feedback"
                >
                    {submitted.correct ? (
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 border border-fa-mint bg-fa-mint/20 flex items-center justify-center">
                                <Check size={18} weight="bold" className="text-fa-mint" />
                            </div>
                            <div>
                                <div className="font-mono text-xs uppercase tracking-widest text-fa-mint">
                                    Trefa · +{submitted.score} bodů
                                </div>
                                <div className="font-mono text-xl text-fa-cream mt-1">
                                    {player.answer}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 border border-red-400 bg-red-400/10 flex items-center justify-center">
                                <X size={18} weight="bold" className="text-red-400" />
                            </div>
                            <div>
                                <div className="font-mono text-xs uppercase tracking-widest text-red-300">
                                    {submitted.reason === "timeout"
                                        ? "Čas vypršel"
                                        : submitted.reason === "skip"
                                        ? "Skip · 0 bodů"
                                        : "Špatně · 0 bodů"}
                                </div>
                                <div className="font-mono text-xl text-fa-cream mt-1">
                                    Správně: {player.answer}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bonus chips — only shown on correct guesses with earned bonuses */}
                    {submitted.correct && (submitted.bonuses || []).length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="inko-bonus-chips">
                            {submitted.bonuses.map((b) => (
                                <div
                                    key={b.id}
                                    data-testid={`inko-bonus-${b.id}`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-fa-yellow bg-fa-yellow/15 font-mono text-[11px] uppercase tracking-widest text-fa-yellow"
                                >
                                    <Star size={11} weight="fill" />
                                    {b.label}
                                    <span className="text-fa-cream">+{b.points} b</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Cross-fantasy lore box — surfaces the European fantasy story
                        (UCL Fantasy / EURO / Bundesliga Fantasy…) that was hidden
                        in `cross_fantasy_profile_phase1` until now. */}
                    {(() => {
                        const lore = getCrossFantasyLore(player);
                        if (!lore || (!lore.summary && !lore.hook)) return null;
                        return (
                            <div
                                className="mt-4 border border-fa-cyan/40 bg-fa-cyan/5 p-3"
                                data-testid="inko-cross-fantasy-lore"
                            >
                                <div className="mb-2 flex items-center gap-2 flex-wrap">
                                    <div className="inline-flex items-center gap-2 px-2 py-1 border border-fa-cyan bg-fa-cyan/15 font-mono text-[10px] uppercase tracking-widest text-fa-cyan">
                                        <Globe size={11} weight="bold" /> European Fantasy
                                    </div>
                                    {lore.competitions.slice(0, 3).map((c) => (
                                        <span
                                            key={c}
                                            className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                                {lore.summary && (
                                    <div className="font-mono text-xs text-fa-cream leading-snug">
                                        {lore.summary}
                                    </div>
                                )}
                                {lore.hook && lore.hook !== lore.summary && (
                                    <div className="mt-1 font-mono text-[11px] text-fa-cream/70 leading-snug">
                                        {lore.hook}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Rare manager badge — clarifies why the score just doubled. */}
                    {submitted.correct && isManager(player) && (
                        <div
                            className="mt-4 border border-fa-yellow/40 bg-fa-yellow/5 p-3"
                            data-testid="inko-manager-lore"
                        >
                            <div className="mb-1 inline-flex items-center gap-2 px-2 py-1 border border-fa-yellow bg-fa-yellow/15 font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                                <Star size={11} weight="fill" /> Rare Manager · 2× body
                            </div>
                            {player.story && (
                                <div className="mt-1 font-mono text-xs text-fa-cream leading-snug">
                                    {player.story}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Death Mode (FPL Pain) badge + lore — shown regardless of
                        correct/wrong, because the story IS the value here. */}
                    {isPain(player) && (
                        <div
                            className="mt-4 border border-red-400/40 bg-red-400/5 p-3"
                            data-testid="inko-pain-lore"
                        >
                            <div className="mb-1 flex items-center gap-2 flex-wrap">
                                <div className="inline-flex items-center gap-2 px-2 py-1 border border-red-400 bg-red-400/15 font-mono text-[10px] uppercase tracking-widest text-red-300">
                                    <Flag size={11} weight="fill" /> Death Mode
                                </div>
                                {player.severity === "legendary" && (
                                    <div className="inline-flex items-center gap-2 px-2 py-1 border border-fa-yellow bg-fa-yellow/15 font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                                        Legendary Pain
                                    </div>
                                )}
                                {player.severity === "high" && (
                                    <div className="inline-flex items-center gap-2 px-2 py-1 border border-orange-400 bg-orange-400/15 font-mono text-[10px] uppercase tracking-widest text-orange-300">
                                        High Pain
                                    </div>
                                )}
                            </div>
                            {player.story && (
                                <div className="mt-1 font-mono text-xs text-fa-cream leading-snug">
                                    {player.story}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Generic "report bug on this player" button */}
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => handleReportClue(0)}
                            disabled={reportedClues.has(0)}
                            data-testid="inko-report-player-btn"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-2 ${
                                reportedClues.has(0)
                                    ? "border-fa-yellow/40 text-fa-yellow/60 cursor-default"
                                    : "border-white/15 text-white/50 hover:border-fa-yellow hover:text-fa-yellow"
                            }`}
                        >
                            <Flag size={11} weight={reportedClues.has(0) ? "fill" : "regular"} />
                            {reportedClues.has(0) ? "Nahlášeno · díky" : "Nahlásit chybu"}
                        </button>
                        <button
                            type="button"
                            onClick={continueNow}
                            data-testid="inko-continue-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-1.5 border border-fa-mint bg-fa-mint/15 text-fa-mint hover:bg-fa-mint hover:text-black transition-colors font-bold"
                        >
                            Pokračovat →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function HardInput({ guess, setGuess, onSubmit, disabled, allPlayers }) {
    const inputRef = useRef(null);
    const [highlighted, setHighlighted] = useState(-1);
    const [showList, setShowList] = useState(false);

    const suggestions = useMemo(() => {
        if (!guess.trim() || guess.trim().length < 2) return [];
        return autocompleteSearch(guess, allPlayers, 5);
    }, [guess, allPlayers]);

    useEffect(() => {
        if (!disabled && inputRef.current) inputRef.current.focus();
    }, [disabled]);

    useEffect(() => { setHighlighted(-1); }, [suggestions.length]);

    const pickSuggestion = (val) => {
        setGuess(val);
        setShowList(false);
        // Return focus to input so user can review or press Enter
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (!showList || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, -1));
        } else if (e.key === "Enter" && highlighted >= 0) {
            e.preventDefault();
            pickSuggestion(suggestions[highlighted]);
        } else if (e.key === "Escape") {
            setShowList(false);
        }
    };

    return (
        <div className="relative">
            <form
                onSubmit={(e) => { e.preventDefault(); onSubmit(guess); setShowList(false); }}
                className="flex items-center gap-2"
            >
                <input
                    ref={inputRef}
                    type="text"
                    value={guess}
                    onChange={(e) => { setGuess(e.target.value); setShowList(true); }}
                    onFocus={() => setShowList(true)}
                    onBlur={() => setTimeout(() => setShowList(false), 200)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    data-testid="inko-hard-input"
                    placeholder="Zadej jméno hráče (KDB, Salah, Sigurdsson…)"
                    autoComplete="off"
                    className="flex-1 bg-black/60 border border-white/15 focus:border-fa-cream outline-none font-mono text-sm text-fa-cream px-3 py-2 placeholder:text-white/25 disabled:opacity-40"
                />
                <button
                    type="submit"
                    disabled={disabled || !guess.trim()}
                    data-testid="inko-hard-submit-btn"
                    className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-30 transition-colors"
                >
                    Uhodnout
                </button>
            </form>

            {showList && suggestions.length > 0 && !disabled && (
                <div
                    className="absolute z-20 left-0 right-0 mt-1 border border-white/15 bg-black/95 backdrop-blur-sm shadow-lg"
                    data-testid="inko-autocomplete"
                >
                    {suggestions.map((name, i) => (
                        <button
                            key={name}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickSuggestion(name); }}
                            onMouseEnter={() => setHighlighted(i)}
                            data-testid={`inko-autocomplete-${normalize(name)}`}
                            className={`w-full text-left px-3 py-2 font-mono text-sm border-b border-white/5 last:border-b-0 transition-colors ${
                                highlighted === i ? "bg-fa-cream/10 text-fa-cream" : "text-white/70 hover:text-fa-cream"
                            }`}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---- Result screen ------------------------------------------------------

function ResultScreen({ results, totalScore, mode, readingMode, onRestart, isDaily10 = false, daily10Date = null }) {
    const correctCount = results.filter((r) => r.correct).length;
    const isFullGame = results.length === ROUNDS_PER_GAME;
    const endBonuses = useMemo(() => computeEndGameBonuses(results), [results]);
    const endBonusPoints = endBonuses.reduce((s, b) => s + b.points, 0);
    const grandTotal = totalScore + endBonusPoints;
    const titles = useMemo(() => computeTitles(results, grandTotal), [results, grandTotal]);

    const maxScore = ROUNDS_PER_GAME * SCORE_TABLE[0];
    const percent = Math.round((grandTotal / maxScore) * 100);

    let grade = "F";
    let gradeColor = "text-red-300";
    if (percent >= 85) { grade = "S"; gradeColor = "text-fa-yellow"; }
    else if (percent >= 70) { grade = "A"; gradeColor = "text-fa-mint"; }
    else if (percent >= 55) { grade = "B"; gradeColor = "text-fa-mint"; }
    else if (percent >= 40) { grade = "C"; gradeColor = "text-fa-cream"; }
    else if (percent >= 25) { grade = "D"; gradeColor = "text-orange-300"; }

    // Display name + submit
    const [displayName, setDisplayName] = useState(
        () => localStorage.getItem(LS_DISPLAY_NAME) || ""
    );
    const [submitState, setSubmitState] = useState("idle"); // idle | submitting | done | error
    const [submitInfo, setSubmitInfo] = useState(null); // {status, previous_best}

    // Share URL for the daily-10 variant (works even before submit).
    const shareUrl =
        typeof window !== "undefined" && isDaily10 && daily10Date
            ? `${window.location.origin}/inko/daily-10?date=${daily10Date}`
            : "";
    const [copied, setCopied] = useState(false);
    const handleCopyShare = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.warn("share copy failed", err?.message);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (submitState === "submitting" || submitState === "done") return;
        const name = displayName.trim() || "Anonym";
        localStorage.setItem(LS_DISPLAY_NAME, name);
        setSubmitState("submitting");
        try {
            const endpoint = isDaily10 ? `${API}/inko/daily10/submit` : `${API}/inko/classic/submit`;
            const body = {
                mode,
                reading_mode: readingMode,
                total_score: grandTotal,
                correct_count: correctCount,
                rounds_played: results.length,
                display_name: name,
                client_id: ensureClientId(),
                ...(isDaily10 ? { date: daily10Date } : {}),
            };
            const r = await axios.post(endpoint, body);
            setSubmitInfo(r.data);
            setSubmitState("done");
        } catch (err) {
            console.warn("submit failed", err?.message);
            setSubmitState("error");
        }
    };

    // Leaderboard state — pre-filled with current combo
    const [lbMode, setLbMode] = useState(mode);
    const [lbReadingMode, setLbReadingMode] = useState(readingMode);
    const [leaderboard, setLeaderboard] = useState(null);
    const [lbLoading, setLbLoading] = useState(true);

    useEffect(() => {
        setLbLoading(true);
        (async () => {
            try {
                const params = new URLSearchParams({
                    mode: lbMode,
                    reading_mode: lbReadingMode,
                    limit: "10",
                    ...(isDaily10 && daily10Date ? { date: daily10Date } : {}),
                });
                const endpoint = isDaily10
                    ? `${API}/inko/daily10/leaderboard?${params}`
                    : `${API}/inko/classic/leaderboard?${params}`;
                const r = await axios.get(endpoint);
                setLeaderboard(r.data);
            } catch {
                setLeaderboard({ rows: [], total_players: 0 });
            } finally {
                setLbLoading(false);
            }
        })();
    }, [lbMode, lbReadingMode, submitState, isDaily10, daily10Date]);

    return (
        <div className="space-y-6">
        <div className="border border-white/10 bg-black/40 p-4 sm:p-8" data-testid="inko-result-screen">
            <div className="mb-6 flex items-center gap-3">
                <Trophy size={20} weight="bold" className="text-fa-yellow" />
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                    Konec hry · {mode === "easy" ? "Easy" : mode === "medium" ? "Medium" : "Hard"} mód
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <ScoreCard
                    label="Celkem bodů"
                    value={grandTotal}
                    suffix={endBonusPoints > 0 ? `(+${endBonusPoints})` : `/ ${maxScore}`}
                    testid="inko-final-score"
                    highlight
                />
                <ScoreCard
                    label="Správně"
                    value={correctCount}
                    suffix={`/ ${results.length}`}
                    testid="inko-correct-count"
                />
                <div className="border border-white/10 bg-black/60 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                        Známka
                    </div>
                    <div
                        className={`font-mono text-4xl mt-1 ${gradeColor}`}
                        data-testid="inko-grade"
                    >
                        {grade}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-wide text-white/40 mt-1">
                        {percent}%
                    </div>
                </div>
            </div>

            {/* End-game bonuses (FPL Oracle, No Hints) */}
            {endBonuses.length > 0 && (
                <div className="mb-6 border border-fa-yellow/40 bg-fa-yellow/5 p-4" data-testid="inko-end-bonuses">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow mb-3 flex items-center gap-2">
                        <Star size={11} weight="fill" /> Bonusy za hru · +{endBonusPoints} b
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {endBonuses.map((b) => (
                            <div
                                key={b.id}
                                data-testid={`inko-end-bonus-${b.id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 border border-fa-yellow bg-fa-yellow/15 font-mono text-[11px] uppercase tracking-widest text-fa-yellow"
                            >
                                <Star size={11} weight="fill" />
                                {b.label}
                                <span className="text-fa-cream">+{b.points} b</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Titles (Clean Sheet, INKO Vision, Budget Genius, …) */}
            {titles.length > 0 && (
                <div className="mb-6 border border-fa-mint/40 bg-fa-mint/5 p-4" data-testid="inko-titles">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint mb-3 flex items-center gap-2">
                        <Trophy size={11} weight="fill" /> Získané tituly · {titles.length}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {titles.map((t) => (
                            <div
                                key={t.id}
                                data-testid={`inko-title-${t.id}`}
                                className="inline-flex flex-col gap-0.5 px-3 py-2 border border-fa-mint bg-fa-mint/15"
                            >
                                <span className="font-mono text-[11px] uppercase tracking-widest text-fa-mint">
                                    {t.label}
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-wide text-fa-cream/70">
                                    {t.desc}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="border-t border-white/10 pt-4 mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                    Podrobný rozpis
                </div>
                <div className="space-y-1" data-testid="inko-round-breakdown">
                    {results.map((r, i) => {
                        const wrongDetail =
                            !r.correct
                                ? r.reason === "timeout"
                                    ? "čas vypršel"
                                    : r.reason === "skip"
                                    ? "přeskočeno"
                                    : r.guess
                                    ? `tvoje odpověď: „${r.guess}“`
                                    : "bez odpovědi"
                                : null;
                        return (
                        <div
                            key={i}
                            className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2"
                            data-testid={`inko-breakdown-row-${i}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="font-mono text-[10px] text-white/30 w-6">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                {r.correct ? (
                                    <Check size={14} weight="bold" className="text-fa-mint shrink-0" />
                                ) : (
                                    <X size={14} weight="bold" className="text-red-400 shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <div className="font-mono text-sm text-fa-cream truncate">
                                        {r.player.answer}
                                    </div>
                                    {wrongDetail && (
                                        <div
                                            className="font-mono text-[10px] uppercase tracking-wide text-red-300/70 truncate"
                                            data-testid={`inko-breakdown-wrong-${i}`}
                                        >
                                            {wrongDetail}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    {r.cluesRevealed} indícií
                                </div>
                                <div
                                    className={`font-mono text-sm ${
                                        r.correct ? "text-fa-mint" : "text-white/30"
                                    }`}
                                >
                                    +{r.score}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={onRestart}
                    data-testid="inko-restart-btn"
                    className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-fa-cream text-fa-cream hover:bg-fa-cream hover:text-black transition-colors flex items-center gap-2"
                >
                    <ArrowClockwise size={12} weight="bold" /> Zahrát znovu
                </button>
                <Link
                    to="/inko"
                    data-testid="inko-back-home-result"
                    className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/60 hover:border-fa-cream hover:text-fa-cream transition-colors flex items-center gap-2"
                >
                    <ArrowLeft size={12} weight="bold" /> INKO Start
                </Link>
            </div>
        </div>

        {/* Submit + leaderboard (all-time for /inko, per-day for /inko/daily-10) */}
        <div className="border border-white/10 bg-black/40 p-4 sm:p-6" data-testid={isDaily10 ? "inko-daily10-leaderboard" : "inko-classic-leaderboard"}>
            <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Trophy size={16} weight="bold" className="text-fa-yellow" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                        {isDaily10 ? `Denní 10 žebříček · ${daily10Date || "…"}` : "All-time žebříček"}
                    </span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {leaderboard?.total_players ?? 0} hráčů v této kombinaci
                </div>
            </div>

            {isDaily10 && shareUrl && (
                <button
                    type="button"
                    onClick={handleCopyShare}
                    data-testid="inko-daily10-share-btn"
                    className="mb-4 w-full flex items-center justify-between gap-2 border border-fa-yellow/40 bg-fa-yellow/10 hover:bg-fa-yellow hover:text-black transition-colors px-3 py-2 text-left group"
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow group-hover:text-black">
                            {copied ? "✔ Zkopírováno" : "Vyzvi kamaráda"}
                        </div>
                        <div className="mt-0.5 font-mono text-[11px] text-white/70 group-hover:text-black truncate">
                            {shareUrl}
                        </div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow group-hover:text-black shrink-0">
                        {copied ? "OK" : "Kopírovat"}
                    </span>
                </button>
            )}

            {isFullGame && submitState !== "done" && (
                <form
                    onSubmit={handleSubmit}
                    className="mb-4 flex flex-wrap items-center gap-2 border border-fa-mint/30 bg-fa-mint/5 p-3"
                    data-testid="inko-classic-submit-form"
                >
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint/80 shrink-0">
                        Přidej se
                    </span>
                    <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value.slice(0, 32))}
                        placeholder="Přezdívka"
                        maxLength={32}
                        data-testid="inko-classic-name-input"
                        className="font-mono text-sm bg-black/50 border border-white/20 px-2 py-1 text-fa-cream focus:outline-none focus:border-fa-mint flex-1 min-w-[8rem]"
                    />
                    <button
                        type="submit"
                        disabled={submitState === "submitting"}
                        data-testid="inko-classic-submit-btn"
                        className="font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black transition-colors disabled:opacity-40"
                    >
                        {submitState === "submitting" ? "Posílám…" : "Zapsat do žebříčku"}
                    </button>
                    {submitState === "error" && (
                        <span className="font-mono text-[10px] uppercase text-red-300">
                            Chyba · zkus to znovu
                        </span>
                    )}
                </form>
            )}
            {submitState === "done" && (() => {
                const status = submitInfo?.status;
                const prev = submitInfo?.previous_best?.total_score;
                let icon = "✔";
                let msg = "Zapsáno do žebříčku";
                let border = "border-fa-mint/40 bg-fa-mint/10 text-fa-mint";
                if (status === "updated_best") {
                    icon = "🏆";
                    msg = prev != null
                        ? `Nový osobní rekord · ${prev} → ${totalScore}`
                        : "Nový osobní rekord";
                    border = "border-fa-yellow/50 bg-fa-yellow/10 text-fa-yellow";
                } else if (status === "unchanged" || status === "updated_name") {
                    icon = "⇢";
                    msg = prev != null && prev >= totalScore
                        ? `Zachováno tvé nejlepší skóre ${prev} (nové ${totalScore} je nižší)`
                        : "Aktualizováno (skóre beze změny)";
                    border = "border-white/20 bg-white/5 text-white/70";
                }
                return (
                    <div
                        className={`mb-4 border p-3 font-mono text-[11px] uppercase tracking-widest ${border}`}
                        data-testid="inko-classic-submit-done"
                    >
                        {icon} {msg}
                    </div>
                );
            })()}

            {/* Leaderboard is auto-locked to the tier just played — no picker,
                so the podium always reflects what the player actually did. */}
            {(() => {
                const playedTier = getTier(lbMode, lbReadingMode);
                return (
                    <div
                        className="mb-4 inline-flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-1.5"
                        data-testid="inko-classic-lb-tier-badge"
                    >
                        <Trophy size={11} weight="fill" className="text-fa-yellow" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                            Žebříček · {playedTier ? playedTier.label : `${lbMode} · ${lbReadingMode}`}
                        </span>
                    </div>
                );
            })()}

            {lbLoading ? (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">Načítám…</div>
            ) : (leaderboard?.rows?.length ?? 0) === 0 ? (
                <div
                    className="font-mono text-[10px] uppercase tracking-widest text-white/40"
                    data-testid="inko-classic-lb-empty"
                >
                    Zatím žádné výsledky v této kombinaci — buď první.
                </div>
            ) : (
                <div className="space-y-1" data-testid="inko-classic-lb-rows">
                    {leaderboard.rows.map((r, i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2"
                            data-testid={`inko-classic-lb-row-${i}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="font-mono text-[10px] text-white/30 w-6">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="font-mono text-sm text-fa-cream truncate">
                                    {r.display_name}
                                </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    {r.correct_count}/{r.rounds_played}
                                </div>
                                <div className="font-mono text-sm text-fa-mint">
                                    {r.total_score}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </div>
    );
}

function ScoreCard({ label, value, suffix, testid, highlight }) {
    return (
        <div
            className={`border p-4 ${
                highlight ? "border-fa-mint/50 bg-fa-mint/5" : "border-white/10 bg-black/60"
            }`}
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                {label}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
                <div
                    className={`font-mono text-4xl ${
                        highlight ? "text-fa-mint" : "text-fa-cream"
                    }`}
                    data-testid={testid}
                >
                    {value}
                </div>
                {suffix && (
                    <div className="font-mono text-sm text-white/40">{suffix}</div>
                )}
            </div>
        </div>
    );
}
